import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/axios';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const sessionExpired = new URLSearchParams(location.search).get('reason') === 'session-expired';

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await api.post('/auth/login', {
        username,
        password,
      });

      const { token, user } = res.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      window.dispatchEvent(new Event('auth-changed'));

      toast.success(`Welcome back, ${user.username}`);
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-12">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-12 rounded-3xl border border-slate-800 bg-slate-900/60 p-8 text-white lg:flex-row">
        <div className="order-2 flex-1 space-y-6 lg:order-1">
          <p className="text-sm uppercase tracking-[0.35em] text-slate-500">Ofoodi POS</p>
          <h1 className="text-4xl font-semibold text-white">Sign in to your shift</h1>
          <p className="text-slate-400">
            Access the modern sales workspace, manage catalogue data, and keep WooCommerce in sync.
            Sessions stay active for 12 hours, so you can focus on serving customers.
          </p>
          <ul className="space-y-3 text-sm text-slate-300">
            {[
              'Full-screen EPOS workspace built for touch and keyboard.',
              'Real-time product, tax, and category management.',
              'Secure access with admin-managed roles and permissions.',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-emerald-400" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="order-1 w-full max-w-md rounded-2xl border border-slate-800 bg-slate-950/70 p-8 shadow-2xl lg:order-2">
          <h2 className="text-2xl font-semibold text-white">Login</h2>
          <p className="mt-1 text-sm text-slate-400">Enter your operator credentials.</p>

          {sessionExpired && (
            <div
              className="mt-5 rounded-2xl border border-amber-400/50 bg-amber-500/10 px-4 py-3 text-sm font-medium text-amber-100"
              role="alert"
              aria-live="assertive"
            >
              Session expired — please sign in again.
            </div>
          )}

          <form onSubmit={handleLogin} className="mt-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Username</label>
              <input
                type="text"
                className="mt-2 w-full rounded-2xl border border-slate-800 bg-slate-900 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Password</label>
              <input
                type="password"
                className="mt-2 w-full rounded-2xl border border-slate-800 bg-slate-900 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-2xl bg-emerald-500 py-3 text-center text-base font-semibold text-slate-900 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <p className="mt-4 text-center text-sm text-slate-400">
            <Link to="/reset-password" className="font-semibold text-emerald-300 hover:text-emerald-200">
              Forgot your password?
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
